import { immer } from 'zustand/middleware/immer'
import { createWithEqualityFn as create } from 'zustand/traditional'

interface CacheStore {
	cache: Record<string, Record<string, unknown>>
	clear: () => void
	append: <DataItem extends { id: string | number }>({
		entity,
		valueProperty,
	}: {
		entity: string
		valueProperty: string | number | symbol
	}) => (data: DataItem[]) => void
}

const emptyObject = {}

const useCacheStore = create<CacheStore>()(
	immer((set) => ({
		cache: {},
		clear: () => {
			set((state) => {
				state.cache = {}
			})
		},
		append: ({ entity, valueProperty = 'id' }) => {
			const cacheKey = getCacheKey(entity, valueProperty)

			return (data) => {
				set((state) => {
					if (!state.cache[cacheKey]) {
						state.cache[cacheKey] = {}
					}

					const cacheEntry = state.cache[cacheKey]

					for (const item of data) {
						cacheEntry[item[valueProperty]] = item
					}
				})
			}
		},
	})),
)

export function useEntityCache<TEntity>(
	entity: string,
	valueProperty?: string | number | symbol,
) {
	const cacheKey = getCacheKey(entity, valueProperty)
	const cache = useCacheStore((s) => s.cache[cacheKey] ?? emptyObject)

	return cache as Record<string, TEntity>
}

export function appendToCache<T extends { id: string | number }>(
	entity: string,
	valueProperty: string | number | symbol = 'id',
) {
	const cacheKey = getCacheKey(entity, valueProperty)
	const append = useCacheStore.getState().append<T>({ entity, valueProperty })

	return (data: T[]) => {
		if (!data?.length) {
			return
		}

		append(data)

		const result = useCacheStore.getState().cache[cacheKey]

		return result
	}
}

export function getMissingIds(
	entity: string,
	ids: Array<string | number>,
	valueProperty?: string | number | symbol,
) {
	const { cache } = useCacheStore.getState()
	const cacheKey = getCacheKey(entity, valueProperty)
	const cachedData = cache[cacheKey] ?? {}

	const missing = ids?.filter((id) => !cachedData[id]) ?? ids

	return missing
}

export function clearCache() {
	useCacheStore.getState().clear()
}

const getCacheKey = (
	entity: string,
	valueProperty: string | number | symbol = 'id',
) => `${entity}(${String(valueProperty)})`
